import { useTableSelect } from "@/hooks/useSelectTable";
import { useCartStore } from "@/store/CartStore";
import { MenuType } from "@/types/menu";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";

interface LocationState {
  orderType: MenuType | null;
  tableId: string | null;
  selectedMenuId: string | null;
  name: string | null;
  setOrderType: (type: MenuType | null) => void;
  setTableId: (table: string) => void;
  setSelectedMenu: (menuId: string) => void;
  setName: (name: string) => void;
  reset: () => void;
}

const LocationContext = createContext<LocationState | undefined>(undefined);

export function LocationContextProvider({ children }: { children: ReactNode }) {
  const { selectedTableId, setSelectedTableId } = useTableSelect();

  const queryTable = selectedTableId ?? null;

  const { locationName, table: cartTable, mode } = useCartStore();
  const [orderType, setOrderType] = useState<MenuType | null>(mode || null);
  const [tableId, setTableId] = useState<string | null>(
    queryTable || cartTable || null
  );
  const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(locationName);

  const cartStore = useCartStore();

  const reset = () => {
    setOrderType(null);
    setTableId(null);
    setSelectedMenuId(null);
    setName(null);
  };

  useEffect(() => {
    //console.log("LocationContextProvider: table", { cartTable, queryTable });

    if (queryTable && !cartTable) {
      console.error(
        "ERROR: Query table on mount, but no cartTable, setting tableId:",
        queryTable
      );
      //handleTableChange(queryTable);
    }

    if (cartTable && !queryTable) {
      console.error(
        "ERROR: Cart table on mount, but no queryTable, un-selecting table"
      );
      //handleTableChange(null);
    }

    // In case we have a SessionStorage cart, but user browses with different table in Query, the query should override the cart table.
    if (queryTable && queryTable !== cartTable) {
      console.warn(
        "Query table is different from cart table, setting cart table to query table",
        {
          queryTable,
          cartTable,
        }
      );
      cartStore.setTable(queryTable);

      if (orderType === "TAKE_AWAY") {
        console.warn(
          "Order type was take away but we found table mismatch between cart & query, setting to DINE_IN"
        );
        handleOrderTypeChange("DINE_IN");
      }
    }

    // If the order type isn't DINE_IN, yet we have a table in Query, we need to set it to DINE_IN, assuming the user is trying to order from a different table.
    if (queryTable && orderType !== "DINE_IN") {
      console.warn(
        "Order isn't dine in, but there is table in query. Setting to DINE_IN"
      );
      handleOrderTypeChange("DINE_IN");
    }
  }, [queryTable, cartTable]);

  function handleOrderTypeChange(type: MenuType | null) {
    //console.info("Changing order type & clearing cart", { type });
    setOrderType(type);
    cartStore.removeAll(); // When changing order type, we need to reset the cart to prevent ordering of items from the previous order type
    cartStore.setMode(type);

    if (type === "TAKE_AWAY") {
      //console.log("Resetting table");
      setTableId(null);
      setSelectedTableId(null);
      cartStore.resetTable();
      /*if (queryTable) {
        console.log(
          "Navigating via window.location to clear table after change to take-away..."
        );
        window.location.href =
          window.location.origin + window.location.pathname; // This is a workaround to force a reload of the page, since the router doesn't handle the query string.
      }*/
    }
  }

  function handleTableChange(value: string | null) {
    setTableId(value);
    setSelectedTableId(value);
    value ? cartStore.setTable(value) : cartStore.resetTable();
  }

  return (
    <LocationContext.Provider
      value={{
        orderType,
        tableId,
        selectedMenuId,
        name,
        setOrderType: handleOrderTypeChange,
        setTableId: handleTableChange,
        setSelectedMenu: setSelectedMenuId,
        setName: (value) => {
          setName(value);
          cartStore.setLocationName(value);
        },
        reset,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
};
